import React from 'react';
import {RawGenericDatePicker} from '../RawGenericDatePicker';
import {Icon} from '../../../../elements/Icon/Icon';
import {GenericDatePicker} from '../GenericDatePicker';

import {ErrorMessage, useField} from 'formik';
import {endOfISOWeek, isSameISOWeek, startOfISOWeek} from 'date-fns';
import {FormContainer} from '../../../form-containers/FormGroup';
import {FormErrorMessage} from '../../../form-labels/FormErrorMessage';

/**
 * @param {object} param0
 * @param {string} [param0.name]
 * Ensure to include the `name` property when using a Formik-type form control. Without it, the form control may not function properly.
 * Additionally, remember to wrap `<Formik></Formik>` around this and all other form control types to ensure proper functionality.
 * Adding the name to a Formik WeekPicker component is essential because Formik uses the name prop to link the value of the  WeekPicker to the Formik state.
 * Note that the Formik type is the default version injected into the form control you are applying.
 *
 * @param {boolean} [param0.raw]
 * If set to true, a non-Formik version of the form control will be injected instead.
 *
 * @param {React.ReactNode} [param0.label]
 * The text to be displayed as the label.
 *
 * @param {string} [param0.placeholder]
 * The text displayed in the Weekpicker when it is empty and not focused, providing a hint to the user about what information is expected.
 * Placeholder text follows a naming convention for consistency:
 *   - 'Select an option'
 *   - 'Pick an time'
 *
 * Avoid text like this:
 * --- Week ----
 *
 * @param {boolean} [param0.weekPicker=false]
 * Enables a week picker mode in the datepicker, allowing users to select entire weeks instead of individual dates.
 *
 * @param {Date} [param0.startDate]
 * Sets the initial selectable start date.
 *
 * @param {Date} [param0.endDate]
 * Sets the maximum selectable end date in the datepicker for date range selection.
 *
 * @param {function} [param0.setSelectedDates]
 * A function to set selected dates in the datepicker programmatically.
 *
 * @param {Date[]} [param0.selectedDates=[]]
 * An array containing currently selected dates in the datepicker.
 */

export function WeekPicker({
    disabled,
    endDate,
    label,
    name,
    raw,
    selectedDates,
    setSelectedDates,
    startDate,
    weekPicker,
    validationError,
}) {
    const [, fieldMeta, fieldHelpers] = useField(name);
    return (
        <FormContainer
            disabled={disabled}
            errorMessage={
                raw ? (
                    <>{validationError && <FormErrorMessage>{validationError}</FormErrorMessage>}</>
                ) : (
                    <ErrorMessage name={name} component={FormErrorMessage} />
                )
            }
            label={label}
            suffix={
                <>
                    <Icon name="calendarMonth" />
                </>
            }>
            {raw ? (
                <RawGenericDatePicker
                    dateFormat={'dd MMM yyyy'}
                    disabled={disabled}
                    endDate={endDate}
                    selectsRange={true}
                    startDate={startDate}
                    weekPicker={weekPicker}
                    specialClass="form-datepicker--Week-picker"
                />
            ) : (
                <GenericDatePicker
                    dateFormat={'dd MMM yyyy'}
                    dayClassName={(date) =>
                        isSameISOWeek(date, new Date(+fieldMeta.value)) ? 'react-datepicker__day--selected' : ''
                    }
                    disabled={disabled}
                    endDate={fieldMeta.value ? endOfISOWeek(new Date(+fieldMeta.value)) : null}
                    name={name}
                    onChange={(v) => fieldHelpers.setValue(Date.parse(v))}
                    selected={fieldMeta.value ? new Date(+fieldMeta.value) : null}
                    selectedDates={selectedDates}
                    selectsRange={true}
                    setSelectedDates={setSelectedDates}
                    showTimeSelect={false}
                    showWeekNumbers={false}
                    startDate={fieldMeta.value ? startOfISOWeek(new Date(+fieldMeta.value)) : null}
                    weekPicker
                    specialClass="form-datepicker--Week-picker"
                />
            )}
        </FormContainer>
    );
}
